import { createSelector } from '@reduxjs/toolkit';
import { differenceInMinutes } from 'date-fns';
import { ClientRole } from 'domain/Broadcast';
import { EventQuestionAssignmentType, EventStatus } from 'domain/event';
import { selectActiveEventDetails } from 'store/apis/event/selectors';
import { selectUserAccount } from 'user/api';

import { EventPhase, NatteringPhase, NatteringStatus } from './types';
import { findRoomById, isHost } from './utils';

import type { RootState } from 'store';

const selectEventState = (state: RootState) => state.event;

export const selectActiveEventId = (state: RootState) => state.event.activeEventId;
export const selectRooms = (state: RootState) => state.event.rooms;
export const selectRoomsCount = (state: RootState) => state.event.roomsCount;
export const selectActiveRoom = (state: RootState) => state.event.activeRoom;
export const selectActiveRoomId = (state: RootState) => state.event.activeRoom?.id;
export const selectActiveRoomDetails = createSelector([selectRooms, selectActiveRoomId], (connectedRooms, roomId) =>
  findRoomById(connectedRooms, roomId)
);
export const selectEndOfRoomQuestionsAssignment = createSelector(selectActiveRoomDetails, (activeRoom) =>
  activeRoom?.roomQuestionAssignments?.filter(
    ({ assignmentType }) => assignmentType === EventQuestionAssignmentType.EndOfRoom
  )
);
export const selectRoomQuestionAssignment = createSelector(selectActiveRoomDetails, (activeRoom) =>
  activeRoom?.roomQuestionAssignments?.find(({ assignmentType }) => assignmentType === EventQuestionAssignmentType.Room)
);
export const selectRoomsRequestingSupport = (state: RootState) => state.event.roomsRequestingSupport;
export const selectDisplayRoomHelpCenter = (state: RootState) => state.event.displayRoomHelpCenter;
export const selectRematchCountdown = (state: RootState) => state.event.rematchCountdown;
export const selectNatteringResumeCountdown = (state: RootState) => state.event.natteringResumeCountdown;
export const selectIsRoomSupportRequested = createSelector(
  [selectRoomsRequestingSupport, (_, roomId: string | undefined) => roomId],
  (roomsRequestingSupport, roomId) => (roomId ? roomsRequestingSupport.includes(roomId) : false)
);
export const selectPinValue = (state: RootState) => state.event.pin;
export const selectBroadcast = (state: RootState) => state.event.broadcast;
export const selectEventMetrics = (state: RootState) => state.event.metrics;
export const selectIsBroadcastCountdown = (state: RootState) => !!state.event.activeRoom?.isBroadcastCountdown;
export const selectServerTimeOffset = (state: RootState) => state.event.serverTimeOffset;

export const selectScreensharingDetails = (state: RootState) => state.event.screensharingDetails;
export const selectActiveRoomGuests = (state: RootState) => state.event.lastCallGuests;
export const selectRoomIdToRate = (state: RootState) => state.event.roomIdToRate;

export const selectAudience = (state: RootState) => state.event.audience;
export const selectSpeakers = (state: RootState) => state.event.speakers;
export const selectIsParticipantSpeaker = createSelector(
  [selectSpeakers, (state: RootState, participantId: number) => participantId],
  (speakers, participantId) => speakers.some((s) => s.id === participantId)
);
export const selectParticipantsWithRaisedHands = (state: RootState) => state.event.participantsWithRaisedHands;
export const selectSpeakersWithRaisedHands = createSelector(
  [selectSpeakers, selectParticipantsWithRaisedHands],
  (speakers, participantsWithRaisedHands) => speakers.filter((s) => participantsWithRaisedHands.includes(s.id))
);
export const selectAudienceWithRaisedHands = createSelector(
  [selectAudience, selectParticipantsWithRaisedHands],
  (audience, participantsWithRaisedHands) => audience.filter((a) => participantsWithRaisedHands.includes(a.id))
);
export const selectStageInvitation = (state: RootState) => state.event.stageInvitation;
export const selectIsJoiningNattering = (state: RootState) => state.event.isJoiningNattering;
export const selectNatteringStatus = createSelector(selectEventState, (state) => state.natteringStatus);
export const selectNatteringSkipConfirmActive = (state: RootState) => state.event.natteringSkipConfirmActive;
const selectIsNatteringSkipped = createSelector(selectNatteringStatus, (status) => status === NatteringStatus.Skipped);
export const selectIsWaitingForNattering = (state: RootState) => state.event.isWaitingForNattering;
export const selectNatteringRoundEndTime = (state: RootState) => state.event.natteringRoundEndTime;
export const selectNatteringRoundStartTime = (state: RootState) => state.event.natteringRoundStartTime;
export const selectIsFirstNatteringRound = (state: RootState) => state.event.natteringRoundId === 1;
export const selectNatteringRoundDurationInMinutes = createSelector(
  selectNatteringRoundEndTime,
  selectNatteringRoundStartTime,
  (endTime, startTime) => {
    if (!endTime || endTime === 0) return;
    if (!startTime || startTime === 0) return;
    return differenceInMinutes(endTime, startTime, { roundingMethod: 'round' });
  }
);
export const selectNatteringRoundTopic = (state: RootState) => state.event.natteringRoundTopic;
export const selectIsBigMeetingMode = (state: RootState) => state.event.isBigMeetingMode;
export const selectIsBroadcastBeingRecorded = (state: RootState) => !!state.event.broadcast?.isRecording;
export const selectAudienceCount = (state: RootState) => state.event.audienceCount;

export const selectParticipantsInvitedToStage = (state: RootState) => state.event.participantsInvitedToStage;
export const selectIsParticipantInvitedToStage = createSelector(
  [selectParticipantsInvitedToStage, (state: RootState, participantId: number) => participantId],
  (participantsInvitedToStage, participantId) => participantsInvitedToStage.includes(participantId)
);

export const selectIsParticipantHandRaised = createSelector(
  [selectParticipantsWithRaisedHands, (state: RootState, participantId: number | undefined) => participantId],
  (participantsWithRaisedHands, participantId) =>
    participantId === undefined ? false : participantsWithRaisedHands.includes(participantId)
);
export const selectRaisedHandsAmount = (state: RootState) => state.event.participantsWithRaisedHands.length;
export const selectIsAnyParticipantHandRaised = (state: RootState) =>
  state.event.participantsWithRaisedHands.length > 0;
export const selectParticipantsDisconnected = (state: RootState) => state.event.participantsDisconnected;
export const selectIsParticipantDisconnected = createSelector(
  [selectParticipantsDisconnected, (state: RootState, participantId: number | undefined) => participantId],
  (participantsDisconnected, participantId) =>
    participantId === undefined ? false : participantsDisconnected.includes(participantId)
);

export const selectShowAudienceList = (state: RootState) => state.event.showAudienceList;
export const selectActivePanelTab = (state: RootState) => state.event.activePanelTab;
export const selectShowTranscriptionModal = (state: RootState) => !!state.event.activeRoom?.showTranscriptionModal;
export const selectIsTranscribing = (state: RootState) => state.event.activeRoom?.isTranscribing ?? false;

export const selectEventHosts = createSelector(selectActiveEventDetails, (activeEvent) => activeEvent?.hosts);

export const selectIsUserEventHost = createSelector(
  selectEventHosts,
  (_state: RootState, userId: number | undefined) => userId,
  (hosts, userId) => isHost(hosts, userId)
);
export const selectIsCurrentUserEventHost = createSelector([selectEventHosts, selectUserAccount], (hosts, user) =>
  isHost(hosts, user?.id)
);

export const selectCurrentEventPhase = createSelector(
  selectEventState,
  selectActiveEventDetails,
  (eventState, eventDetails) => {
    if (eventDetails?.status === EventStatus.Finished) return EventPhase.Ended;

    return eventState.eventPhase;
  }
);

export const selectIsOnBackstage = createSelector(
  selectCurrentEventPhase,
  selectIsCurrentUserEventHost,
  selectActiveRoomDetails,
  (eventPhase, isHost, activeRoomDetails) => {
    if (eventPhase !== EventPhase.Nattering || !isHost || (isHost && activeRoomDetails?.id)) return false;

    return true;
  }
);

export const selectBroadcastRole = createSelector(
  selectBroadcast,
  (broadcast): ClientRole => broadcast?.role ?? ClientRole.Audience
);
export const selectSpeakersSorted = createSelector(
  [selectSpeakers, selectParticipantsWithRaisedHands],
  (speakers, participantsWithRaisedHands) =>
    [...speakers].sort((s) => (participantsWithRaisedHands.includes(s.id) ? -1 : 1))
);
export const selectParticipants = createSelector([selectAudience, selectSpeakersSorted], (audience, speakers) =>
  audience.concat(speakers)
);
export const selectParticipantById = createSelector(
  (_state: RootState, participantId?: number) => participantId,
  selectParticipants,
  (participantId, participants) => participants.find((p) => p.id === participantId)
);
export const selectRaisedHandAlertText = createSelector(
  selectParticipantsWithRaisedHands,
  selectParticipants,
  (participantsWithRaisedHands, participants) => {
    const firstParticipantsWithRaisedHand = participantsWithRaisedHands.slice(0, 2);
    const participantNames = firstParticipantsWithRaisedHand.length
      ? participants.filter((p) => firstParticipantsWithRaisedHand.includes(p.id)).map((p) => p.name)
      : [];
    switch (participantsWithRaisedHands.length) {
      case 0:
        return `${participantsWithRaisedHands.length} people have raised hands`;
      case 1:
        return `${participantNames[0]} has raised a hand`;
      case 2:
        return `${participantNames.join(' and ')} have raised hands`;
      default:
        return `${participantNames.join(', ')} and ${participantsWithRaisedHands.length - participantNames.length} more have raised hands`;
    }
  }
);
export const selectSkippedNatterQuestionsAssignments = (state: RootState) => state.event.questionsAssignments;
export const selectCurrentNatteringRoundTopic = (state: RootState) => state.event.questionsAssignments[0]?.title;

export const selectActiveRoomRegularAttendeesBasicInfo = createSelector(
  [selectActiveRoomDetails, selectUserAccount, selectEventHosts],
  (roomDetails, user, hosts) =>
    [
      {
        name: user?.displayName,
        id: user?.id,
      },
      ...(roomDetails?.attendees ?? []).map(({ name, id }) => ({
        name,
        id,
      })),
    ].filter(({ id }) => id && hosts && !hosts.some(({ accountId }) => accountId === id))
);

// The room being rated after a natter round ends. Sourced from `roomIdToRate` rather than the
// accumulated `lastCallGuests` so that, after a rematch, we only show the partners from the last
// room and not everyone met across every room in the round.
export const selectRoomToRateDetails = createSelector([selectRooms, selectRoomIdToRate], (rooms, roomIdToRate) =>
  findRoomById(rooms, roomIdToRate)
);

export const selectGuestsToRate = createSelector(
  [selectRoomToRateDetails, selectUserAccount, selectEventHosts],
  (room, user, hosts) => (room?.attendees ?? []).filter(({ id }) => id !== user?.id && !isHost(hosts, id))
);

export const selectActiveSpeakersIds = (state: RootState) => state.event.activeSpeakersIds;

export const selectIsMeetingStateUnknown = createSelector(
  selectBroadcast,
  selectActiveRoom,
  selectIsJoiningNattering,
  selectIsNatteringSkipped,
  selectIsWaitingForNattering,
  (broadcast, activeRoom, isJoiningNattering, isNatteringSkipped, isWaitingForNattering) =>
    !broadcast && !activeRoom && !isJoiningNattering && !isNatteringSkipped && !isWaitingForNattering
);

export const selectNatteringPhase = createSelector(selectEventState, (state) => state.natteringPhase);

export const selectShouldShowTopicTeaser = createSelector(
  selectNatteringPhase,
  selectNatteringStatus,
  selectNatteringRoundTopic,
  (phase, status, topic) =>
    phase === NatteringPhase.WaitingToStart &&
    [NatteringStatus.Accepted, NatteringStatus.Skipped].includes(status) &&
    !!topic
);

export const selectShouldShowNatteringSkipped = createSelector(
  selectNatteringPhase,
  selectNatteringStatus,
  (phase, status) => phase === NatteringPhase.InProgress && status === NatteringStatus.Skipped
);

export const selectIsHostInRoom = createSelector(selectActiveRoomDetails, selectEventHosts, (activeRoom, hosts) => {
  if (!activeRoom || !hosts) return false;
  return activeRoom.attendees.some(({ id }) => hosts.some(({ accountId }) => id === accountId));
});

export const selectIsScreensharing = (state: RootState) => state.event.isScreensharing;
